<template>
  <div
    :class="[
      'form-input__container',
      $props?.is_error ? 'form-input__container_error' : '',
      $props?.is_small ? 'form-input__container_small' : ''
    ]"
  >
    <label v-if="label" class="form-input__label">
      {{ label }} <span class="form-input__required" v-show="$props?.is_required">*</span>
    </label>
    <div class="form-input__wrapper">
      <input
        :class="[
          'form-input__input',
          $props?.is_disabled ? 'form-input__input_disabled' : '',
          $props.icon_name.length > 0 || $props.is_show_icon ? 'form-input__input_icon' : ''
         ]"
        :type="$props.inputType || 'text'"
        :placeholder="$props?.placeholder"
        :max="$props.max"
        v-model="_input_value"
        @keydown="updateValue"
        @paste="updateOnPaste"
        @input="updateValue"
        @blur="$emit('blur')"
        @focus="$emit('focus')"
        @focusout="onFocusOut"
      >
      <div v-if="$props.icon_name.length > 0 || $props.is_show_icon" class="form-input__icon-container">
        <slot name="icon">
          <component
            class="form-input__search-icon"
            :is="asyncIconComponent"
            @click="$emit('startSearch')"
          ></component>
        </slot>
      </div>
    </div>
    <div
      v-if="$props.helper_text || $props.is_error"
      :class="['form-input__helper-text', $props?.is_error ? 'form-input__helper-text_error' : '' ]"
    >
      <slot name="error">{{ ($props?.is_error) ? $props.error_message : $props.helper_text }}</slot>
    </div>
  </div>
</template>

<script setup>
import { ref, defineAsyncComponent, watch, onMounted } from "vue";

const $props = defineProps({
  modelValue: [String, Number],
  max: {
    type: Number,
    default: null
  },
  label: String,
  type: String,
  inputType: String,
  placeholder: String,
  error_message: String,
  helper_text: String,
  is_show_icon: {
    type: Boolean,
    default: false
  },
  is_small: {
    type: Boolean,
    default: false
  },
  is_error: {
    type: Boolean,
    default: false
  },
  is_required: {
    type: Boolean,
    default: false
  },
  is_disabled: {
    type: Boolean,
    default: false
  },
  is_search_input: {
    type: Boolean,
    default: false
  }, isPreventDefault: {
      type: Boolean,
      default: false
    },
  icon_name: {
    type: String,
    default: ""
  },
  bindData: [String, Number],
  withIgnoreKeys: {
    type: Boolean,
    default: false
  }
})

const $emit = defineEmits(["update:modelValue", "startSearch", "input","inputEmit","focusout","focus","blur"])
const _input_value = ref($props.modelValue)
const ignoreKeys = ['Backspace','CapsLock', 'ArrowUp','ArrowDown','ArrowLeft','ArrowRight','Shift']
const asyncIconComponent = ($props.icon_name.length > 0) ? defineAsyncComponent(() => import(`@/components/ui/icons/${$props?.icon_name}.vue`)
  .catch((error) => {
    console.log(error)
    return "";
  })
) : null;

function checkValidation() {
  $emit("input", _input_value.value)
}

function onFocusOut(event){
  $emit("focusout", event, _input_value.value)
  checkValidation()
}
function updateOnPaste(e) {

  let regExp = null

  if ($props.type === "number") {
    regExp = /[^0-9]/g
  }

  if ($props.type === "text") {
    regExp = /[^a-zA-Z\u0400-\u04FF ]+/g
  }

  if (regExp && regExp.test(e.clipboardData.getData('text'))) {
    e.preventDefault()
  }
}

function updateValue(event) {
  if($props.withIgnoreKeys && event.key && ignoreKeys.includes(event.key)){
    return
  }

  $emit("inputEmit", event)
  let regExp = null

  if ($props.type === "number") {
    regExp = /[^0-9]/g
  }

  if ($props.type === "text") {
    regExp = /[^a-zA-Z\u0400-\u04FF ]+/g
  }

  if (regExp && regExp.test(event.key) && event.key !== 'Backspace') {
    event.preventDefault()
    return
  }

  event.target.value = event.target.value.replace(regExp, '')

  if($props.max > 0 && event.target.value.length >= $props.max){
    if($props.isPreventDefault && event.key !== 'Backspace') {
      event.preventDefault()
    } else {
      _input_value.value = event.target.value.substr(0, $props.max - 1)
    }

  } else {
    _input_value.value = event.target.value
  }
}

watch($props, () => {
  if(typeof $props?.modelValue === 'string' || typeof $props?.modelValue === 'number'){
    _input_value.value = $props.modelValue
  }
  if(typeof $props?.bindData === 'string'){
    _input_value.value = $props.bindData
  }
},{deep: true})

watch(_input_value, () => {
  $emit("update:modelValue", _input_value.value)
})

onMounted(() => {
  if(typeof $props?.bindData === 'string'){
    _input_value.value = $props.bindData
  }
})
</script>

<style lang="scss" scoped>
.form-input {

  &__container {
    position: relative;
    display: flex;
    flex-direction: column;

    &_error {

      .form-input__label {
        color: #F15A22;
      }

      .form-input__input {
        border-color: #F15A22;
      }
    }

    &_small {

      .form-input__input {
        height: 40px;
      }
    }
  }

  &__label {
    font-size: 12px;
    line-height: 20px;
    transition: color .2s;

    & + .form-input__wrapper {
      margin: 4px 0 0 0;
    }
  }

  &__required {
    margin: 0 0 0 4px;
    color: var(--error-color);
  }

  &__wrapper {
    position: relative;
  }

  &__icon-container {
    position: absolute;
    right: 12px;
    top: 0;
    width: 24px;
    height: 100%;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    user-select: none;
  }

  &__helper-text {
    font-size: 12px;
    line-height: 20px;
    color: var(--font-color-two);
    transition: color .2s;

    &_error {
      color: #F15A22;
    }
  }

  &__search-icon {
    width: 24px;
    height: 24px;
    color: var(--font-color-two);
    transition: color .2s;

    &:hover {
      color: var(--hover-color);
    }
  }

  &__input {
    border: 1px solid var(--border-color);
    border-radius: 8px;
    width: 100%;
    height: 48px;
    padding: 8px 16px 8px 16px;
    font-size: 14px;
    transition: border-color .2s, box-shadow .2s;
    -webkit-text-size-adjust: none;
    text-size-adjust: none;

    @include screenM {
      font-size: 16px;
    }

    &[type="number"] {

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      -moz-appearance: textfield;
    }

    &::placeholder {
      font-size: 14px;
      line-height: 20px;
      transition: color .2s;
      color: var(--font-color-two);
    }

    &:hover {
      border-color: var(--hover-color);
    }

    &:focus {
      outline: none;
      border-color: var(--hover-color);
      box-shadow: 0 0 0 4px #E4E8F4;
    }

    &_disabled {
      user-select: none;
      pointer-events: none;
      background-color: var(--background-color);
      color: var(--font-color-secondary);
    }

    &_icon {
      padding: 8px 40px 8px 16px;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
